import { lazy, Suspense } from 'react'

import { createFileRoute } from '@tanstack/react-router'
import { zodValidator } from '@tanstack/zod-adapter'

import { z } from 'zod'

import { ErrorBoundary } from '@/components/common/error-boundary'
import { Skeleton } from '@/components/ui/skeleton'

const DetailsInvestigationSteps = lazy(() =>
	import('@/features/air/case-management/components/investigation-steps/details-investigation-steps').then(
		(m) => ({ default: m.DetailsInvestigationSteps }),
	),
)

/**
 * `trace` — whether the timeline shows `sub_agent_trace` steps ("Show reasoning
 * trace"). It lives in the URL rather than component state because it changes
 * what the page *shows*: a link to this tab can then carry the reasoning trace
 * open (the demo tour's S10 narrates it, and an analyst can share a deep link to
 * the reasoning behind a step). Absent = off; the toggle drops the param rather
 * than writing `trace=false`, so the plain URL stays clean.
 *
 * `.catch(undefined)` degrades a hand-edited `?trace=garbage` to "off" instead
 * of failing the route — a view preference is not worth an error screen.
 */
const searchSchema = z.object({
	trace: z.boolean().optional().catch(undefined),
})

export const Route = createFileRoute(
	'/_dashboard/air/case-management/$id/investigation-steps',
)({
	validateSearch: zodValidator(searchSchema),
	head: ({ params }) => ({
		meta: [
			{
				title: `Investigation Steps · Case ${params.id} | Case Management | BitLyft AIR®`,
			},
		],
	}),
	component: RouteComponent,
})

function RouteComponent() {
	const { id } = Route.useParams()

	return (
		<ErrorBoundary
			feature="case-management"
			section="investigation-steps"
			entityId={id}
		>
			<Suspense fallback={<Skeleton className="h-64 w-full" />}>
				<DetailsInvestigationSteps id={id} />
			</Suspense>
		</ErrorBoundary>
	)
}
